/**
 * <FieldReportCard>
 *
 * The tile shown in the Cards view of the Field Reports page. Extracted
 * from FieldReports.js after the 4 tab bodies (PreCon / Soft Dig /
 * Locate / Confined Space) had drifted from each other four separate
 * times — see PRD entry 2026-02-26 "4-Form-Type Consistency Audit".
 *
 * Contract (caller owns all data + navigation):
 * - kind          'precon' | 'softdig' | 'locate' | 'cse'
 * - kindLabel     'PreCon' | 'Soft Dig' | 'Locate' | 'Confined Space'
 * - kindColor     Tailwind class pair for the kind pill
 *                 (e.g. 'bg-blue-100 text-blue-700')
 * - item          the report object (used only for status + testid + PhotoReadinessDot)
 * - title         computed title to render (bold line)
 * - location      computed location to render (grey line under title)
 * - meta          array of { label: 'Team', value: '-' } lines to show
 * - onView        () => void — card body + View button
 * - onEdit        () => void — Edit button
 * - onPdf         () => void — PDF button (blob download / preview route)
 * - onEmail       () => void — Email PDF button
 * - onReset       () => void | null — Reset Approval (admin, only if approved)
 * - onDelete      () => void | null — Delete (admin)
 * - isAdmin       bool — gates Reset + Delete
 * - PhotoReadinessDot  React component (kept as a prop so this file
 *                      doesn't need to know how photo readiness is
 *                      computed — that logic stays in FieldReports.js)
 */
import React from 'react';
import { Edit, Eye, Download, Mail, RotateCcw, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { resolveStatus } from './status';

export default function FieldReportCard({
  kind,
  kindLabel,
  kindColor,
  item,
  title,
  location,
  meta = [],
  onView,
  onEdit,
  onPdf,
  onEmail,
  onReset,
  onDelete,
  isAdmin,
  PhotoReadinessDot,
}) {
  const status = resolveStatus(item.status);
  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(); };

  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={onView}
      data-testid={`${kind}-card-${item.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className={`${kindColor} whitespace-nowrap`}>{kindLabel}</Badge>
              <Badge className={status.color}>{status.label}</Badge>
              {PhotoReadinessDot && <PhotoReadinessDot record={item} />}
            </div>
            <h3 className="font-semibold text-slate-900" data-testid="field-report-card-title">{title}</h3>
            <p className="text-sm text-slate-500">{location}</p>
          </div>
        </div>
        {meta.length > 0 && (
          <div className="text-xs text-slate-400 space-y-1">
            {meta.map(({ label, value }) => (
              <p key={label}>{label}: {value ?? '-'}</p>
            ))}
          </div>
        )}
        {/* Row 1 — Edit + View (Edit hidden when onEdit isn't provided,
            e.g. the Approved tab where reports are read-only.) */}
        <div className="flex gap-2 mt-3">
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={stop(onEdit)}
              data-testid={`${kind}-edit-${item.id}`}
            >
              <Edit className="w-3 h-3 mr-1" />
              Edit
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={stop(onView)}
            data-testid={`${kind}-view-${item.id}`}
          >
            <Eye className="w-3 h-3 mr-1" />
            View
          </Button>
        </div>
        {/* Row 2 — PDF, Email, Reset (admin, approved), Delete (admin) */}
        <div className="flex gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-amber-600 border-amber-300 hover:bg-amber-50"
            onClick={stop(onPdf)}
            data-testid={`${kind}-pdf-${item.id}`}
          >
            <Download className="w-3 h-3 mr-1" />
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50"
            onClick={stop(onEmail)}
            data-testid={`${kind}-email-pdf-${item.id}`}
          >
            <Mail className="w-3 h-3 mr-1" />
            Email
          </Button>
          {isAdmin && item.status === 'completed' && onReset && (
            <Button
              variant="outline"
              size="sm"
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              onClick={stop(onReset)}
              title="Reset Approval"
              data-testid={`reset-approval-${kind}-${item.id}`}
            >
              <RotateCcw className="w-3 h-3" />
            </Button>
          )}
          {isAdmin && onDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={stop(onDelete)}
              data-testid={`${kind}-delete-${item.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
